/* eslint-disable jira/filename-case */
import React, { type ComponentType } from 'react';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { projectLayout } from '@jira/platform__layouts-project-layout/src/index.tsx';
import {
	composeLayouts,
	withProjectContext,
	type Layout,
} from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { SOFTWARE_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';

// TODO Remove this whenever all software project route paths are no longer ambiguous
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const withAnySoftwareProjectContext = (Component: ComponentType<Record<any, any>>) =>
	withProjectContext(Component, {
		project: {
			style: 'any',
			type: SOFTWARE_PROJECT,
		},
	});

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const Onboarding = lazyAfterPaint(
	() =>
		import(/* webpackChunkName: "async-software-project-onboarding" */ './ui/onboarding/index.tsx'),
	{ ssr: false },
);

Onboarding.displayName = 'Lazy(ui/onboarding/index.tsx)';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const OnboardingCode = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-software-project-onboarding-code" */ './ui/onboarding-code/OnboardingCode.tsx'
		),
	{ ssr: false },
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const RightSidebars = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-software-project-right-sidebars" */ './ui/right-sidebars/RightSidebars.tsx'
		),
	{ ssr: false },
);

RightSidebars.displayName = 'Lazy(ui/right-sidebars/RightSidebars.tsx)';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const RightSidebarsCode = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-software-project-right-sidebars-code" */ './ui/right-sidebars-code/RightSidebarsCode.tsx'
		),
	{ ssr: false },
);

const OnboardingWithSoftwareAsExpectedProjectType = (
	props: React.ComponentProps<typeof Onboarding>,
) => <Onboarding {...props} expectedProjectType={SOFTWARE_PROJECT} />;

const OnboardingCodeWithSoftwareAsExpectedProjectType = (
	props: React.ComponentProps<typeof OnboardingCode>,
) => <OnboardingCode {...props} expectedProjectType={SOFTWARE_PROJECT} />;

const SoftWareOnboarding = componentWithFG(
	'jira-fix-quickstart-not-showing',
	OnboardingWithSoftwareAsExpectedProjectType,
	withAnySoftwareProjectContext(Onboarding),
);

const SoftWareOnboardingCode = componentWithFG(
	'jira-fix-quickstart-not-showing',
	OnboardingCodeWithSoftwareAsExpectedProjectType,
	withAnySoftwareProjectContext(OnboardingCode),
);

const nonSettingsRightSidebar = componentWithFG(
	'jira-fix-quickstart-not-showing',
	RightSidebars,
	withAnySoftwareProjectContext(RightSidebars),
);
nonSettingsRightSidebar.displayName = 'NonSettingsRightSidebar';

const nonSettingsRightSidebarCode = componentWithFG(
	'jira-fix-quickstart-not-showing',
	RightSidebarsCode,
	withAnySoftwareProjectContext(RightSidebarsCode),
);

const sharedSoftwareProjectLayout: Layout = composeLayouts(projectLayout, {});

export const classicSoftwareProjectLayout: Layout = composeLayouts(sharedSoftwareProjectLayout, {
	rightSidebars: [nonSettingsRightSidebar],
	globalComponents: [SoftWareOnboarding],
});

export const softwareProjectLayout: Layout = composeLayouts(sharedSoftwareProjectLayout, {
	rightSidebars: [nonSettingsRightSidebar],
	globalComponents: [SoftWareOnboarding],
});

export const softwareProjectLayoutNoOnboarding: Layout = composeLayouts(
	sharedSoftwareProjectLayout,
);

export const classicSoftwareProjectLayoutNoOnboarding: Layout = composeLayouts(
	sharedSoftwareProjectLayout,
);

/**
 * Conditional onboarding - displays Quickstart in CiJ for Devops Bundle
 */
export const softwareProjectLayoutCode: Layout = composeLayouts(sharedSoftwareProjectLayout, {
	rightSidebars: [nonSettingsRightSidebarCode],
	globalComponents: [SoftWareOnboardingCode],
});
